// @ts-nocheck

(function () {
  'use strict';
  // Toggle to true while debugging locally
  const DEBUG = false;
  const log = (...args) => DEBUG && console.log('[observers]', ...args);

  // Run a callback when DOM is ready
  function onDomReady(cb) {
    if (document.readyState !== 'loading') cb();
    else document.addEventListener('DOMContentLoaded', cb, { once: true });
  }

  /**
   * Wait for an element to appear in the DOM.
   * Resolves with the first matching Element.
   */
  function waitFor(selector, { root = document, timeout = null } = {}) {
    return new Promise((resolve, reject) => {
      const found = root.querySelector(selector);
      if (found) {
        log('found', selector);
        resolve(found);
        return;
      }

      const obs = new MutationObserver(() => {
        const el = root.querySelector(selector);
        if (el) {
          obs.disconnect();
          resolve(el);
        }
      });
      obs.observe(root, { childList: true, subtree: true });

      if (timeout != null) {
        setTimeout(() => {
          obs.disconnect();
          reject(new Error(`Timeout waiting for ${selector}`));
        }, timeout);
      }
    });
  }

  /**
   * Fix accessibility issues inside #zrl_container:
   *  - aria-hidden="true" elements must not be focusable -> remove tabindex
   *  - specifically remove tabindex from #zrl_ld_close_button if present
   *  - keep watching for dynamically added nodes inside the container
   */
  function handleZrlContainer(container) {
    // Fix 1: any aria-hidden="true" AND focusable -> remove tabindex
    container.querySelectorAll('[aria-hidden="true"][tabindex]').forEach((el) => {
      el.removeAttribute('tabindex');
    });

    // Fix 2: specific close button id
    const closeBtn = container.querySelector('#zrl_ld_close_button');
    if (closeBtn && closeBtn.hasAttribute('tabindex')) {
      closeBtn.removeAttribute('tabindex');
    }

    // Observe inside the container for dynamic inserts
    const innerObs = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          // Node itself may match
          if (node.matches?.('[aria-hidden="true"][tabindex]')) {
            node.removeAttribute('tabindex');
            // log('inner: removed tabindex from aria-hidden element', node);
          }
          if (node.id === 'zrl_ld_close_button' && node.hasAttribute('tabindex')) {
            node.removeAttribute('tabindex');
            // log('inner: removed tabindex from #zrl_ld_close_button');
          }

          // Or its descendants
          node.querySelectorAll?.('[aria-hidden="true"][tabindex]').forEach((el) => {
            el.removeAttribute('tabindex');
            // log('inner: removed tabindex from aria-hidden descendant', el);
          });
          node.querySelectorAll?.('#zrl_ld_close_button[tabindex]').forEach((el) => {
            el.removeAttribute('tabindex');
            // log('inner: removed tabindex from descendant #zrl_ld_close_button');
          });
        });
      });
    });

    innerObs.observe(container, { childList: true, subtree: true });
  }

  const addTitleToChatButton = (container) => {
    container.setAttribute('title', 'Open live chat');
  };

  const TURNTO_FORM_SELECTOR = '.tt-c-review-form__form';
  const TURNTO_SUBMIT_SELECTOR = '.tt-c-review-form__submit';
  const TURNTO_REDIRECT_PARAM_NAMES = ['return_to', 'redirect'];
  const TURNTO_LOGIN_PATH = '/account/login';
  const NEW_ACCOUNTS_LOGIN_PATH = '/customer_authentication/login';
  const TURNTO_REDIRECT_STORAGE_KEY = 'turntoRedirectTarget';

  function getRedirectValue() {
    try {
      const { pathname, search } = window.location;
      if (pathname.startsWith(TURNTO_LOGIN_PATH)) {
        const stored = sessionStorage.getItem(TURNTO_REDIRECT_STORAGE_KEY);
        return stored ?? '';
      }
      return `${pathname}${search}`;
    } catch {
      return '';
    }
  }

  function ensureHiddenField(form, name, value) {
    if (!value) return;

    let hidden = form.querySelector(`input[name="${name}"]`);
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = name;
      form.appendChild(hidden);
    }

    if (hidden.value !== value) hidden.value = value;
  }

  function withLoginRedirectParam(url, redirectValue) {
    if (!redirectValue) return url;

    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.pathname.startsWith(TURNTO_LOGIN_PATH)) {
        return parsed.toString();
      }

      // New customer accounts: Shopify intercepts /account/login and drops its
      // query, so send TurnTo's login redirect to the hosted login instead.
      parsed.pathname = NEW_ACCOUNTS_LOGIN_PATH;

      TURNTO_REDIRECT_PARAM_NAMES.forEach((param) => {
        parsed.searchParams.set(param, redirectValue);
      });

      return parsed.toString();
    } catch {
      return url;
    }
  }

  function applyRedirectParam(form) {
    if (!(form instanceof HTMLFormElement)) return;
    const redirectValue = getRedirectValue();
    if (!redirectValue) return;

    try {
      sessionStorage.setItem(TURNTO_REDIRECT_STORAGE_KEY, redirectValue);
    } catch {
      /* ignore */
    }

    TURNTO_REDIRECT_PARAM_NAMES.forEach((param) => ensureHiddenField(form, param, redirectValue));

    const actionAttr = form.getAttribute('action');
    if (actionAttr) {
      try {
        const actionUrl = new URL(actionAttr, window.location.origin);
        if (actionUrl.pathname.startsWith(TURNTO_LOGIN_PATH)) {
          // New customer accounts: Shopify intercepts /account/login and drops its
          // query, so point the form at the hosted login as well (see
          // withLoginRedirectParam above).
          actionUrl.pathname = NEW_ACCOUNTS_LOGIN_PATH;
          TURNTO_REDIRECT_PARAM_NAMES.forEach((param) => actionUrl.searchParams.set(param, redirectValue));
          form.setAttribute('action', actionUrl.toString());
        }
      } catch (error) {
        log('unable to set redirect on TurnTo form action', error);
      }
    }
  }

  function bindTurnToForm(form) {
    log('bindTurnToForm', form);
    if (!(form instanceof HTMLFormElement) || form.__turntoRedirectApplied) return;
    form.__turntoRedirectApplied = true;

    const update = () => applyRedirectParam(form);
    update();

    const submitButton = form.querySelector(TURNTO_SUBMIT_SELECTOR);
    if (submitButton) {
      submitButton.addEventListener('click', update, { capture: true });
    }
    form.addEventListener('submit', update, { capture: true });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          update();
        }
      }
    });
    observer.observe(form, { attributes: true, attributeFilter: ['action'] });
  }

  function scanForTurnToForms(root = document) {
    log('scanForTurnToForms', root);
    root.querySelectorAll(TURNTO_FORM_SELECTOR).forEach(bindTurnToForm);
  }

  onDomReady(() => {
    const updateLoginLinks = (root = document) => {
      const redirectValue = (() => {
        if (window.location.pathname.startsWith(TURNTO_LOGIN_PATH)) {
          try {
            return sessionStorage.getItem(TURNTO_REDIRECT_STORAGE_KEY) ?? '';
          } catch {
            return '';
          }
        }
        return getRedirectValue();
      })();

      if (!redirectValue) return;

      root
        .querySelectorAll?.(
          `a[href*="${TURNTO_LOGIN_PATH}"], button[data-href*="${TURNTO_LOGIN_PATH}"]`
        )
        .forEach((el) => {
          const attr = el.tagName === 'BUTTON' ? 'data-href' : 'href';
          const current = el.getAttribute(attr);
          if (!current) return;
          const updated = withLoginRedirectParam(current, redirectValue);
          if (updated !== current) {
            el.setAttribute(attr, updated);
          }
        });

      if (!window.location.pathname.startsWith(TURNTO_LOGIN_PATH) && redirectValue) {
        try {
          sessionStorage.setItem(TURNTO_REDIRECT_STORAGE_KEY, redirectValue);
        } catch {
          /* ignore */
        }
      }
    };

    document.body.addEventListener(
      'click',
      (event) => {
        const anchor = event.target.closest?.(`a[href*="${TURNTO_LOGIN_PATH}"]`);
        if (!anchor) return;
        const current = anchor.getAttribute('href');
        if (!current) return;
        const redirectValue = window.location.pathname.startsWith(TURNTO_LOGIN_PATH)
          ? (() => {
              try {
                return sessionStorage.getItem(TURNTO_REDIRECT_STORAGE_KEY) ?? '';
              } catch {
                return '';
              }
            })()
          : getRedirectValue();

        if (redirectValue) {
          try {
            sessionStorage.setItem(TURNTO_REDIRECT_STORAGE_KEY, redirectValue);
          } catch {
            /* ignore */
          }
        }
        const updated = withLoginRedirectParam(current, redirectValue);
        if (updated !== current) {
          anchor.setAttribute('href', updated);
        }
      },
      { capture: true }
    );

    updateLoginLinks();
    scanForTurnToForms();

    const turntoObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.(TURNTO_FORM_SELECTOR)) {
            log('1found turn to form', node);
            bindTurnToForm(node);
          } else {
            log('2found turn to form', node);
            node.querySelectorAll?.(TURNTO_FORM_SELECTOR).forEach(bindTurnToForm);
          }
          updateLoginLinks(node);
        });
      }
    });

    turntoObserver.observe(document.body, { childList: true, subtree: true });
  });

  onDomReady(() => {
    if (!window.location.pathname.startsWith(TURNTO_LOGIN_PATH)) return;

    try {
      const storedRedirect = sessionStorage.getItem(TURNTO_REDIRECT_STORAGE_KEY);
      if (!storedRedirect) return;

      const url = new URL(window.location.href);
      let needsUpdate = false;
      TURNTO_REDIRECT_PARAM_NAMES.forEach((param) => {
        if (!url.searchParams.get(param)) {
          url.searchParams.set(param, storedRedirect);
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        sessionStorage.removeItem(TURNTO_REDIRECT_STORAGE_KEY);
        window.location.replace(url.toString());
      }
    } catch {
      // ignore storage errors
    }
  });
  // Start observers
  onDomReady(() => {
    // Watch once for the container, then attach internal observer
    waitFor('#zrl_container')
      .then(handleZrlContainer)
      .catch(() => {});
    waitFor('#icChatButton')
      .then(addTitleToChatButton)
      .catch(() => {});

    // Also keep an eye on body in case #zrl_container is removed and re-added later
    const bodyObs = new MutationObserver(() => {
      const c = document.querySelector('#zrl_container');
      if (c) handleZrlContainer(c);
    });
    bodyObs.observe(document.body, { childList: true, subtree: true });
  });

  // Additionally: if the close button may exist (or be reinserted) outside the container,
  // apply the same tabindex removal globally as a safety net.
  onDomReady(() => {
    const fixClose = (root = document) => {
      root.querySelectorAll('#zrl_ld_close_button[tabindex]').forEach((el) => {
        el.removeAttribute('tabindex');
        // log('removed tabindex from global #zrl_ld_close_button');
      });
    };

    // Run once now
    fixClose();

    // And observe for future insertions
    const obs = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.id === 'zrl_ld_close_button' && node.hasAttribute('tabindex')) {
            node.removeAttribute('tabindex');
            // log('observer: removed tabindex from #zrl_ld_close_button');
          }
          node.querySelectorAll?.('#zrl_ld_close_button[tabindex]').forEach((el) => {
            el.removeAttribute('tabindex');
            // log('observer: removed tabindex from descendant #zrl_ld_close_button');
          });
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  });
})();

(() => {
  const DRAWER_SEL = '.drawer';
  const NAV_SEL = '.help-actions__nav.sidebar__nav';

  const drawer = document.querySelector(DRAWER_SEL);
  const nav = drawer?.querySelector(NAV_SEL);
  if (!drawer || !nav) return;

  const HINT_ID = 'help-actions-scroll-hint';
  if (!document.getElementById(HINT_ID)) {
    const hint = document.createElement('span');
    hint.id = HINT_ID;
    hint.className = 'sr-only';
    hint.textContent = 'Scrollable area. Use arrow keys to scroll.';
    document.body.appendChild(hint);
  }

  function isOpen(el) {
    const hasOpenClass =
      el.classList.contains('is-open') ||
      el.classList.contains('open') ||
      el.classList.contains('active');
    const ariaOpen =
      el.getAttribute('aria-hidden') === 'false' || el.getAttribute('aria-expanded') === 'true';
    const style = getComputedStyle(el);
    const visible =
      style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    return hasOpenClass || ariaOpen || visible;
  }

  function isScrollable(el) {
    return el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
  }

  function applyFocusableState() {
    if (isOpen(drawer) && isScrollable(nav)) {
      nav.setAttribute('tabindex', '0');
      nav.setAttribute('aria-describedby', HINT_ID);
    } else {
      nav.removeAttribute('tabindex');
      nav.removeAttribute('aria-describedby');
    }
  }

  applyFocusableState();

  const mo = new MutationObserver(applyFocusableState);
  mo.observe(drawer, {
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-hidden', 'aria-expanded'],
  });

  const navMo = new MutationObserver(applyFocusableState);
  navMo.observe(nav, { childList: true, subtree: true });

  window.addEventListener('resize', applyFocusableState);
})();

(() => {
  const CONTAINER_SEL = '.og-container';
  const ITEM_SEL = '.og-order-item, .og-order-item-content';
  const IMG_SEL = '.og-order-item-info .og-thumbnail img, .og-order-item-content .og-thumbnail img';
  const LABEL_SEL = [
    '.og-order-item-info .product-link',
    '.og-order-item-info .og-product-title',
    '.og-order-item-info .og-product-title-sm',
    '.og-order-item-content .product-link',
    '.og-order-item-content .og-product-title',
    '.og-order-item-content .og-product-title-sm',
  ].join(', ');

  const PRICE_SEL = 'span.og-price';

  function patchPrice(el) {
    if (!(el instanceof Element)) return;

    // Compute the visible price text by ignoring any existing sr-only nodes
    const clone = el.cloneNode(true);
    clone.querySelectorAll('.sr-only').forEach((n) => n.remove());
    const priceText = (clone.textContent || '').replace(/\s+/g, ' ').trim();
    if (!priceText) return;

    // Ensure a single sr-only child exists and keep it updated
    let sr = el.querySelector(':scope > .sr-only[data-sr-price="true"]');
    if (!sr) {
      sr = document.createElement('span');
      sr.className = 'sr-only';
      sr.setAttribute('data-sr-price', 'true');
      // Prepend so SR sees it first but remains visually hidden
      el.insertBefore(sr, el.firstChild);
    }
    sr.textContent = `Price: ${priceText}`;

    // Ensure the visible price is inside an aria-hidden span so SR doesn't read it twice
    let vis = el.querySelector(':scope > span[data-visible-price="true"]');
    if (!vis) {
      vis = document.createElement('span');
      vis.setAttribute('data-visible-price', 'true');
      vis.setAttribute('aria-hidden', 'true');
    }
    vis.textContent = priceText;

    // Rebuild contents in the correct order: sr-only first, then visible text
    while (el.firstChild) el.removeChild(el.firstChild);
    el.appendChild(sr);
    el.appendChild(vis);
  }

  function getLabel(el) {
    if (!el) return '';
    if (el.tagName === 'A') {
      const aria = (el.getAttribute('aria-label') || '').trim();
      if (aria) return aria;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
      const title = (el.getAttribute('title') || '').trim();
      if (title) return title;
      try {
        const href = el.getAttribute('href') || '';
        if (href) {
          const last =
            href
              .split(/[\/?#]/)
              .filter(Boolean)
              .pop() || '';
          if (last) return decodeURIComponent(last.replace(/[-_]+/g, ' ')).trim();
        }
      } catch {}
      return '';
    }

    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return text;
  }

  function patchItem(item) {
    if (!(item instanceof Element)) return;
    const img = item.querySelector(IMG_SEL);
    if (!img) return;

    const labelNode = item.querySelector(LABEL_SEL);
    const currentAlt = img.getAttribute('alt');
    const alreadyPatched = img.hasAttribute('data-alt-patched');
    if (alreadyPatched) return;
    if (currentAlt && currentAlt.trim().length > 0) return;

    const label = getLabel(labelNode);
    if (label) {
      img.setAttribute('alt', label);
      img.setAttribute('data-alt-patched', 'true');
    }
  }

  function scanContainer(container) {
    container.querySelectorAll(ITEM_SEL).forEach(patchItem);
    container.querySelectorAll(PRICE_SEL).forEach(patchPrice);
  }

  function observeContainer(container) {
    if (!container || container.__ogAltObserver) return;

    scanContainer(container);

    const itemObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.(ITEM_SEL)) {
            patchItem(node);
          } else {
            node.querySelectorAll?.(ITEM_SEL).forEach(patchItem);
          }
          if (node.matches?.(PRICE_SEL)) {
            patchPrice(node);
          } else {
            node.querySelectorAll?.(PRICE_SEL).forEach(patchPrice);
          }
        });

        if (m.type === 'attributes' && m.target instanceof Element) {
          if (m.target.matches?.(IMG_SEL) || m.target.matches?.(LABEL_SEL)) {
            const item = m.target.closest?.(ITEM_SEL);
            if (item) patchItem(item);
          }
        }
        if (m.type === 'characterData') {
          const parent = m.target.parentElement;
          const priceEl = parent?.closest?.(PRICE_SEL);
          if (priceEl) patchPrice(priceEl);
        }
      }
    });

    itemObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['alt', 'aria-label', 'title', 'href', 'class'],
    });

    container.__ogAltObserver = itemObserver;
  }

  const rootObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches?.(CONTAINER_SEL)) {
          observeContainer(node);
        } else {
          node.querySelectorAll?.(CONTAINER_SEL).forEach(observeContainer);
        }
      });
    }
  });

  function init() {
    document.querySelectorAll(CONTAINER_SEL).forEach(observeContainer);
    rootObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
  const ROOT_SEL = '#BambooHR-ATS .BambooHR-ATS-board';
  const DEPT_LIST_SEL = 'ul.BambooHR-ATS-Department-List';
  const JOBS_LIST_SEL = 'ul.BambooHR-ATS-Jobs-List';
  const HEADER_SEL = '.BambooHR-ATS-Department-Header';
  const H2_SEL = 'h2';

  const norm = (s) => (s || '').replace(/\s+/g, ' ').trim();

  function siblingH2Text(el) {
    let n = el?.previousElementSibling;
    while (n) {
      if (n.matches?.(H2_SEL)) return norm(n.textContent);
      n = n.previousElementSibling;
    }
    n = el?.nextElementSibling;
    while (n) {
      if (n.matches?.(H2_SEL)) return norm(n.textContent);
      n = n.nextElementSibling;
    }
    return '';
  }

  function deptHeaderText(fromEl) {
    const container = fromEl.closest?.('.BambooHR-ATS-Department') || fromEl.parentElement;
    const header = container?.querySelector?.(HEADER_SEL);
    return header ? norm(header.textContent) : '';
  }

  function setAriaLabel(el, text) {
    if (text && el.getAttribute('aria-label') !== text) el.setAttribute('aria-label', text);
  }

  function labelDeptList(ul) {
    if (!(ul instanceof Element) || !ul.matches(DEPT_LIST_SEL)) return;
    const text = siblingH2Text(ul) || deptHeaderText(ul);
    setAriaLabel(ul, text);
    ul.querySelectorAll(JOBS_LIST_SEL).forEach(labelJobsList);
  }

  function labelJobsList(ul) {
    if (!(ul instanceof Element) || !ul.matches(JOBS_LIST_SEL)) return;
    const text = deptHeaderText(ul) || siblingH2Text(ul);
    setAriaLabel(ul, text);
  }

  function scan(rootEl) {
    rootEl.querySelectorAll(DEPT_LIST_SEL).forEach(labelDeptList);
    rootEl.querySelectorAll(JOBS_LIST_SEL).forEach(labelJobsList);
  }

  function start(root) {
    scan(root);
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.(DEPT_LIST_SEL)) labelDeptList(node);
          if (node.matches?.(JOBS_LIST_SEL)) labelJobsList(node);
          node.querySelectorAll?.(DEPT_LIST_SEL).forEach(labelDeptList);
          node.querySelectorAll?.(JOBS_LIST_SEL).forEach(labelJobsList);
        });

        if (
          m.type === 'characterData' ||
          (m.type === 'attributes' && m.target instanceof Element)
        ) {
          const el = m.target.nodeType === 3 ? m.target.parentElement : m.target;
          if (el?.matches?.(HEADER_SEL) || el?.matches?.(H2_SEL)) {
            const container = el.closest?.('.BambooHR-ATS-Department') || root;
            container?.querySelectorAll?.(DEPT_LIST_SEL).forEach(labelDeptList);
            container?.querySelectorAll?.(JOBS_LIST_SEL).forEach(labelJobsList);
          }
        }
      }
    });
    mo.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'id'],
    });
  }

  function waitThenStart() {
    const root = document.querySelector(ROOT_SEL);
    if (root) return start(root);
    const obs = new MutationObserver(() => {
      const r = document.querySelector(ROOT_SEL);
      if (r) {
        obs.disconnect();
        start(r);
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', waitThenStart, { once: true })
    : waitThenStart();
})();

(function () {
  function setAriaLabel(el, label) {
    if (el && label && el.getAttribute('aria-label') !== label) {
      el.setAttribute('aria-label', label);
    }
  }

  function patchRatingNumber(el) {
    if (!(el instanceof Element)) return;
    const value = (el.textContent || '').replace(/\s+/g, ' ').trim();
    let text = '';
    // Try to find sibling .tt-c-reviews-summary__rating-text (either direction)
    let sibling = el.nextElementSibling;
    while (sibling) {
      if (sibling.classList?.contains('tt-c-reviews-summary__rating-text')) {
        text = (sibling.textContent || '').replace(/\s+/g, ' ').trim();
        break;
      }
      sibling = sibling.nextElementSibling;
    }
    if (!text) {
      sibling = el.previousElementSibling;
      while (sibling) {
        if (sibling.classList?.contains('tt-c-reviews-summary__rating-text')) {
          text = (sibling.textContent || '').replace(/\s+/g, ' ').trim();
          break;
        }
        sibling = sibling.previousElementSibling;
      }
    }
    // Find parent .tt-c-reviews-summary__rating.tt-u-mb--sm
    const parent = el.closest('.tt-c-reviews-summary__rating.tt-u-mb--sm');
    if (parent && value) {
      const label = text ? `Rating: ${value} ${text}` : `Rating: ${value}`;
      setAriaLabel(parent, label);
      // Set role="paragraph" on the parent as well
      parent.setAttribute('role', 'paragraph');
      // Hide number and text from screen readers
      if (el.getAttribute('aria-hidden') !== 'true') el.setAttribute('aria-hidden', 'true');
      // Try to find text element as sibling or child
      let textEl = null;
      // Try next/prev sibling
      let sib = el.nextElementSibling;
      while (sib) {
        if (sib.classList?.contains('tt-c-reviews-summary__rating-text')) {
          textEl = sib;
          break;
        }
        sib = sib.nextElementSibling;
      }
      if (!textEl) {
        sib = el.previousElementSibling;
        while (sib) {
          if (sib.classList?.contains('tt-c-reviews-summary__rating-text')) {
            textEl = sib;
            break;
          }
          sib = sib.previousElementSibling;
        }
      }
      // If still not found, try querySelector in parent
      if (!textEl && parent) {
        textEl = parent.querySelector('.tt-c-reviews-summary__rating-text');
      }
      if (textEl && textEl.getAttribute('aria-hidden') !== 'true') {
        textEl.setAttribute('aria-hidden', 'true');
      }
    }
  }

  // Patch .tt-c-reviews-summary__rating-text: aria-hidden="true"
  function patchRatingText(el) {
    if (!(el instanceof Element)) return;
    if (el.getAttribute('aria-hidden') !== 'true') {
      el.setAttribute('aria-hidden', 'true');
    }
    // Also try to find parent .tt-c-reviews-summary__rating.tt-u-mb--sm and patch its aria-label if possible
    // Find sibling .tt-c-reviews-summary__rating-number
    let number = null;
    let sibling = el.previousElementSibling;
    while (sibling) {
      if (sibling.classList?.contains('tt-c-reviews-summary__rating-number')) {
        number = sibling;
        break;
      }
      sibling = sibling.previousElementSibling;
    }
    if (!number) {
      sibling = el.nextElementSibling;
      while (sibling) {
        if (sibling.classList?.contains('tt-c-reviews-summary__rating-number')) {
          number = sibling;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
    }
    if (!number) {
      // Try querySelector in parent
      const parent = el.closest('.tt-c-reviews-summary__rating.tt-u-mb--sm');
      if (parent) {
        number = parent.querySelector('.tt-c-reviews-summary__rating-number');
      }
    }
    if (number) patchRatingNumber(number);
  }

  // Patch .tt-c-dimension__label: combine all labels and value-labels in its container into one aria-label
  function patchDimensionLabels(labelEl) {
    if (!(labelEl instanceof Element) || !labelEl.closest) return;
    // Assume the parentNode is the container
    const container = labelEl.parentElement;
    if (!container) return;
    // Collect all label texts and value-label texts inside this container
    const labelNodes = Array.from(container.querySelectorAll('.tt-c-dimension__label'));
    const valueLabelNodes = Array.from(container.querySelectorAll('.tt-c-dimension__value-label'));
    const allTexts = labelNodes
      .concat(valueLabelNodes)
      .map((el) => (el.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (allTexts.length > 0) {
      setAriaLabel(container, allTexts.join(' '));
      // Set role="paragraph" on the parent container
      container.setAttribute('role', 'paragraph');
    }
  }

  // Patch .product-card__rating .sr-only spans: set role="paragraph"
  function patchProductCardRatings() {
    document.querySelectorAll('.product-card__rating').forEach((card) => {
      card.querySelectorAll('span.sr-only').forEach((span) => {
        span.setAttribute('role', 'paragraph');
      });
    });
  }

  // Scan all existing on page
  function scanAll() {
    // For each .tt-c-reviews-summary__rating.tt-u-mb--sm, patch its children
    document.querySelectorAll('.tt-c-reviews-summary__rating.tt-u-mb--sm').forEach((parent) => {
      const number = parent.querySelector('.tt-c-reviews-summary__rating-number');
      if (number) patchRatingNumber(number);
      const text = parent.querySelector('.tt-c-reviews-summary__rating-text');
      if (text) patchRatingText(text);
    });
    // Fallback in case elements exist outside parent
    document.querySelectorAll('.tt-c-reviews-summary__rating-number').forEach(patchRatingNumber);
    document.querySelectorAll('.tt-c-reviews-summary__rating-text').forEach(patchRatingText);
    document.querySelectorAll('.tt-c-dimension__label').forEach(patchDimensionLabels);
    // Patch product card ratings
    patchProductCardRatings();
  }

  // Observe for dynamic additions
  function observe() {
    const root = document.body;
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          // If a new .tt-c-reviews-summary__rating.tt-u-mb--sm parent is added, patch its children
          if (node.matches?.('.tt-c-reviews-summary__rating.tt-u-mb--sm')) {
            const number = node.querySelector('.tt-c-reviews-summary__rating-number');
            if (number) patchRatingNumber(number);
            const text = node.querySelector('.tt-c-reviews-summary__rating-text');
            if (text) patchRatingText(text);
          }
          node.querySelectorAll?.('.tt-c-reviews-summary__rating.tt-u-mb--sm').forEach((parent) => {
            const number = parent.querySelector('.tt-c-reviews-summary__rating-number');
            if (number) patchRatingNumber(number);
            const text = parent.querySelector('.tt-c-reviews-summary__rating-text');
            if (text) patchRatingText(text);
          });
          // Fallback: patch any loose numbers/texts
          if (node.matches?.('.tt-c-reviews-summary__rating-number')) patchRatingNumber(node);
          node
            .querySelectorAll?.('.tt-c-reviews-summary__rating-number')
            .forEach(patchRatingNumber);
          if (node.matches?.('.tt-c-reviews-summary__rating-text')) patchRatingText(node);
          node.querySelectorAll?.('.tt-c-reviews-summary__rating-text').forEach(patchRatingText);
          // Dimension label
          if (node.matches?.('.tt-c-dimension__label')) patchDimensionLabels(node);
          node.querySelectorAll?.('.tt-c-dimension__label').forEach(patchDimensionLabels);
          // Patch product card ratings for added nodes
          if (node.matches?.('.product-card__rating')) {
            node.querySelectorAll?.('span.sr-only').forEach((span) => {
              span.setAttribute('role', 'paragraph');
            });
          }
          node.querySelectorAll?.('.product-card__rating').forEach((card) => {
            card.querySelectorAll('span.sr-only').forEach((span) => {
              span.setAttribute('role', 'paragraph');
            });
          });
        });
        // If a class is changed, patch if relevant
        if (m.type === 'attributes' && m.target instanceof Element) {
          if (m.target.matches('.tt-c-reviews-summary__rating-number')) patchRatingNumber(m.target);
          if (m.target.matches('.tt-c-reviews-summary__rating-text')) patchRatingText(m.target);
          if (m.target.matches('.tt-c-dimension__label')) patchDimensionLabels(m.target);
          // Patch if relevant for product card ratings
          if (m.target.matches('.product-card__rating')) {
            m.target.querySelectorAll('span.sr-only').forEach((span) => {
              span.setAttribute('role', 'paragraph');
            });
          }
        }
      });
    });
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scanAll();
        observe();
      },
      { once: true }
    );
  } else {
    scanAll();
    observe();
  }
})();
